import React, { useState } from "react";
import DOMPurify from "dompurify";
import CommonButton from "./CommonButton";
import { useRouter } from "next/navigation";

const JobCard = ({ job, onApplyClick }) => {
  const [expanded, setExpanded] = useState(false);
  const router = useRouter();
  const formatSalary = (min, max, currency) => {
    const formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  const formatExperience = (min, max) => {
    if (min === max) return `${min} year${min !== 1 ? "s" : ""}`;
    return `${min}-${max} years`;
  };

  return (
    <div className="modern-job-card">
      {/* Header Section with Gradient Background */}
      <div className="card-header-section">
        <div className="header-badges">
          <span
            className={`modern-status-badge ${
              job.isOpen ? "status-open" : "status-closed"
            }`}
          >
            <span className="badge-dot"></span>
            {job.isOpen ? "Open" : "Closed"}
          </span>
          <span className="location-badge">
            <svg
              className="location-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {job.locationType}
          </span>
        </div>

        <h3 className="modern-job-title">{job.title}</h3>
        <p className="modern-job-team">{job.team}</p>

        {job.customQuestions && job.customQuestions.length > 0 && (
          <div className="custom-questions-pill">
            <svg
              className="question-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {job.customQuestions.length} custom question
            {job.customQuestions.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Details Grid */}
      <div className="details-grid">
        <div className="detail-card salary-card">
          <div className="detail-icon-wrapper">
            <svg
              className="detail-svg"
              xmlns="http://www.w3.org/2000/svg"
              id="Layer_1"
              data-name="Layer 1"
              viewBox="0 0 24 24"
              width="512"
              height="512"
            >
              <path d="M16.5,0c-4.206,0-7.5,1.977-7.5,4.5v2.587c-.484-.057-.985-.087-1.5-.087C3.294,7,0,8.977,0,11.5v8c0,2.523,3.294,4.5,7.5,4.5,3.416,0,6.231-1.304,7.167-3.146,.597,.087,1.207,.146,1.833,.146,4.206,0,7.5-1.977,7.5-4.5V4.5c0-2.523-3.294-4.5-7.5-4.5Zm0,2c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM7.5,9c3.148,0,5.5,1.32,5.5,2.5s-2.352,2.5-5.5,2.5-5.5-1.32-5.5-2.5,2.352-2.5,5.5-2.5ZM2,14.582c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5s-5.5-1.32-5.5-2.5v-.918Zm5.5,7.418c-3.148,0-5.5-1.32-5.5-2.5v-.918c1.36,.875,3.303,1.418,5.5,1.418s4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm9-3c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.514,0-1.012-.047-1.5-.116v-1.98c.492,.058,.99,.096,1.5,.096,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Zm0-4c-.542,0-1.066-.051-1.578-.127-.198-.887-.809-1.684-1.721-2.321,.992,.285,2.106,.449,3.299,.449,2.197,0,4.14-.543,5.5-1.418v.918c0,1.18-2.352,2.5-5.5,2.5Z" />
            </svg>
          </div>
          <div className="detail-content">
            <span className="detail-label">Salary Range</span>
            <span className="detail-value">
              {formatSalary(
                job.salaryRange.min,
                job.salaryRange.max,
                job.salaryRange.currency
              )}
            </span>
            <span className="detail-sublabel">Per Month</span>
          </div>
        </div>

        <div className="detail-card experience-card">
          <div className="detail-icon-wrapper">
            <svg
              className="detail-svg"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div className="detail-content">
            <span className="detail-label">Experience</span>
            <span className="detail-value">
              {formatExperience(job.minExp, job.maxExp)}
            </span>
          </div>
        </div>

        <div className="detail-card positions-card">
          <div className="detail-icon-wrapper">
            <svg
              className="detail-svg"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div className="detail-content">
            <span className="detail-label">Open Positions</span>
            <span className="detail-value">{job.totalPositions}</span>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className={`modern-description ${expanded ? "is-expanded" : ""}`}>
        <div className="d-flex justify-content-between align-items-center">
          <CommonButton
            className="black-border"
            variant="outline"
            onClick={() => router.push(`/career/${job._id}`)}
            type="button"
            label=" Explore More"
          />

          <CommonButton
            label={job.isOpen ? "Apply Now" : "Position Closed"}
            className="btn-primary ms-auto"
            disabled={!job.isOpen}
            onClick={() => onApplyClick(job)}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                style={{ height: "20px" }}
              >
                <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
              </svg>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default JobCard;
